/**
 * Placement-test unit renderer.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import ProblemMock from "pages/board/problems/ProblemMock";
import PositionFirst from "./PositionFirst";
import PositionResult from "./PositionResult";
import { getCorrectProblemBlockCount, getGradeForBlock } from "utils/grade.util";
import { getQAArray } from "utils/course";
import { actionSendSurveyEmail } from "context/action";
import { useAsterController } from "context";

/**
 * Orchestrates placement-test intro, attempt, and result screens.
 *
 * @param {{ xblocks?: Array, onSubmitProblem?: Function }} props
 */
const TestPositionmentRender = (props) => {
  const { xblocks, onSubmitProblem } = props;

  const { t } = useTranslation("common");

  const { courseId } = useParams();

  const [controller, dispatch] = useAsterController();
  const { courses, structure: courseStructure } = controller;

  const currCourse = useMemo(() => (courses.data || []).find((v) => v.id === courseId) || {}, [courses, courseId]);

  const [cursor, setCursor] = useState(-1);
  const [problemBlocks, setProblemBlocks] = useState([]);
  const [correctCount, setCorrectCount] = useState(null);

  const [qaArray, setQAArray] = useState([]);
  const [surveyRes, setSurveyRes] = useState([]);

  useEffect(() => {
    const problems = xblocks.filter((b) => b.type === "problem");
    setProblemBlocks(problems);
    setQAArray(getQAArray(problems));
  }, [xblocks]);

  useEffect(() => {
    if (problemBlocks && problemBlocks.length && cursor == -1) {
      const gradeCounter = getCorrectProblemBlockCount(
        currCourse,
        problemBlocks.map((p) => p.id)
      );
      gradeCounter["haveGraded"] && setCorrectCount(gradeCounter["counter"]);
    }
  }, [problemBlocks, currCourse, cursor]);

  // If continue button clicked
  /**
   * Advances the placement-test flow.
   *
   * @returns {void}
   */
  const onContinueClick = () => {
    if (cursor + 1 === problemBlocks.length) {
      setCorrectCount(
        getCorrectProblemBlockCount(
          currCourse,
          problemBlocks.map((p) => p.id)
        )["counter"]
      );
      actionSendSurveyEmail(dispatch, surveyRes);
    }
    setCursor(cursor + 1);
  };

  /**
   * Captures a placement-test answer and maps it onto the QA array.
   *
   * @param {Object} payload Answer payload.
   * @returns {void}
   */
  const onMockProblemSubmit = (payload) => {
    // Now update survey result
    if (payload.choices && payload.choices.length) {
      const choiceId = payload.choices[0].id;
      const choiceAns = payload.choices[0].student_answer;
      const qaItem = qaArray.find((v) => choiceId.includes(v.id));
      if (qaItem) {
        const option = qaItem.answers.find((v) => v.option === choiceAns);
        setSurveyRes([...surveyRes, { question: qaItem.question, answer: option.text }]);
      }
    }

    onSubmitProblem(payload, "ALLOW_POST");
  };

  return (
    <>
      {correctCount === null ? (
        <>
          {cursor === -1 && <PositionFirst totalCount={problemBlocks.length} onContinueClick={onContinueClick} />}
          {0 <= cursor && cursor < problemBlocks.length && (
            <>
              <div className="text-xs mt-4 text-uppercase">
                {t("Question")}&nbsp;{cursor + 1}/{problemBlocks.length}
              </div>
              <ProblemMock key={cursor} rawText={problemBlocks[cursor].html} grade={getGradeForBlock(currCourse, problemBlocks[cursor].id)} courseId={courseStructure.course_id} blockId={problemBlocks[cursor].id} onSubmitProblem={onMockProblemSubmit} onContinueClick={onContinueClick} />
            </>
          )}
        </>
      ) : (
        <PositionResult totalCount={problemBlocks.length} successCount={correctCount} />
      )}
    </>
  );
};

export default TestPositionmentRender;
